import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CivilLitigationSaveStep4 } from '../../types/civilLitigationApi';
import type { CivilLitigationWizardDraft } from '../../types/civilLitigationCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { formatDateRange, formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { MultiFileReviewDocumentsTable } from '../wizard/civilAdvise/MultiFileReviewDocumentsTable';
import { useCivilLitigationLookups } from '../wizard/civilLitigation/useCivilLitigationLookups';
import { CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS } from '../wizard/civilLitigation/civilLitigationConstants';
import { isOtherCorporateInstitute, isCivilLitigationOriginalCourtsCategory, requiresCourtType } from '../wizard/civilLitigation/civilLitigationHelpers';
import { apiOrLookupLabel, deliveryMethodDisplay, submitterFromApiStep } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: CivilLitigationWizardDraft;
  submittedAt: string;
  step4?: CivilLitigationSaveStep4;
  hideUploadDocumentsStep?: boolean;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function ViewRequestCivilLitigationCaseDetailsTab({
  caseRequestId,
  draft,
  submittedAt,
  step4,
  hideUploadDocumentsStep = false,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useCivilLitigationLookups('review', draft.provinceId);

  const deliveryLabel = useMemo(
    () =>
      deliveryMethodDisplay(
        draft as unknown as CriminalWizardDraft,
        lookups.deliveryMethods,
        step4 as CriminalSaveStep4 | undefined,
        noData,
      ),
    [draft, lookups.deliveryMethods, step4, noData],
  );

  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';

  const corpNames = draft.publicCorporationNames.filter((n) => n.trim()).join(', ');
  const partyBodyTypeLabel =
    draft.involvesPublicCorporation === 'yes'
      ? CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS.incorporated
      : CIVIL_LITIGATION_PARTY_BODY_TYPE_LABELS.government;
  const isOriginalCourts = isCivilLitigationOriginalCourtsCategory(
    draft.fileCategory,
    draft.subFileCategoryName,
  );

  const submitter = useMemo(() => submitterFromApiStep(step4), [step4]);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            <ReviewField label="Institute Type" value={instituteLabel} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Has action been instituted?" value={yesNoLabel(draft.hasActionInstituted)} />
            {requiresCourtType(
              draft.hasActionInstituted,
              draft.subFileCategoryId,
              [],
              draft.subFileCategoryName,
            ) ? (
              <>
                <ReviewField label="Court Type" value={draft.courtTypeName || draft.courtTypeId} />
                {draft.courtTypeOther ? <ReviewField label="Court Type (Other)" value={draft.courtTypeOther} /> : null}
              </>
            ) : null}
            {draft.hasActionInstituted === 'yes' ? (
              <ReviewField label="Court Name" value={draft.courtName || '—'} />
            ) : null}
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={yesNoLabel(draft.hasConnectedMatters)} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label="Connected AGD File Reference Number"
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Party Details">
        <ReviewGrid cols={4}>
          <ReviewField label="Plaintiff / Applicant Name" value={draft.plaintiffName} />
          <ReviewField label="Defendant / Respondent Name" value={draft.defendantName} />
          <ReviewField label="Select one of the following" value={partyBodyTypeLabel} span={2} />
          {!isOriginalCourts && draft.involvesPublicCorporation === 'yes' ? (
            <ReviewField label="Public Corporation Name(s)" value={corpNames} span={2} />
          ) : null}
          <ReviewField label="Other Involved Institutions" value={draft.otherInvolvedInstitutions} span={2} />
          <ReviewField label="Name of the Legal Officer/ Officer In-Charge" value={draft.legalOfficerName} />
          <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Incident Details">
        <ReviewSubBlock title="Incident/ Matter Details">
          <ReviewField label="Subject of the Dispute" value={draft.subjectOfDispute} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Incident Occurred Date"
              value={formatDateRange(draft.incidentDateFrom, draft.incidentDateTo)}
            />
            <ReviewField
              label="Next Step of the Case"
              value={
                draft.nextStepOfCaseOther.trim() ||
                apiOrLookupLabel(draft.nextStepOfCaseName, draft.nextStepOfCaseId, lookups.nextSteps)
              }
            />
            <ReviewField label="Next Date of the Case" value={formatDisplayDate(draft.nextCaseDate)} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      {!hideUploadDocumentsStep ? (
        <ReviewSection step="Step 05" title="Upload Documents">
          <ReviewSubBlock>
            <MultiFileReviewDocumentsTable documents={draft.documents} />
          </ReviewSubBlock>
        </ReviewSection>
      ) : null}
    </div>
  );
}
